import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, Trash2, Edit, Check, Search, Download, Filter,
  Calendar, DollarSign, Building2,
  FileText, RefreshCw, CreditCard, AlertCircle, CheckCircle2,
  TrendingUp, Wallet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { exportToExcel } from "../lib/utils";
import TablePagination from "../components/TablePagination";
import SeletorNatureza from "@/features/despesas/components/SeletorNatureza";
import { CarregandoCards, CarregandoTabela } from "@/components/carregando";
import { Skeleton } from "@/components/ui/skeleton";
import { ROTA_NOVA_DESPESA, rotaEditarDespesa } from "@/features/despesas/nova/rotas";
import { canalPorChave } from "@/components/canais/canais";
import IconeCanal from "@/components/canais/IconeCanal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const API = '/api';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function Despesas() {
  // State para classificações
  const [classifications, setClassifications] = useState([]);
  const [classificationDialogOpen, setClassificationDialogOpen] = useState(false);
  const [editingClassification, setEditingClassification] = useState(null);
  const [classificationName, setClassificationName] = useState("");
  const [classificationToDelete, setClassificationToDelete] = useState(null);
  const [deleteClassificationDialogOpen, setDeleteClassificationDialogOpen] = useState(false);
  
  // State para despesas
  const [expenses, setExpenses] = useState([]);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [deleteExpenseDialogOpen, setDeleteExpenseDialogOpen] = useState(false);
  const [deleteRecurringDialogOpen, setDeleteRecurringDialogOpen] = useState(false); // Novo dialog para despesas recorrentes
  
  // Natureza contabil: obrigatoria desde 25/08/2026 — sem ela a despesa nao
  // entra em nenhuma linha do DRE (Financeiro -> DRE).
  const [classificationNature, setClassificationNature] = useState("");
  
  // Filtros e ordenação
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, pending, paid
  const [classificationFilter, setClassificationFilter] = useState("all");
  // Ao voltar da tela Nova/Editar despesa, reabre no mês que estava aberto.
  const { state: voltaDoForm } = useLocation();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(voltaDoForm?.mes ?? new Date().getMonth()); // 0-11
  const [selectedYear, setSelectedYear] = useState(voltaDoForm?.ano ?? new Date().getFullYear());
  const [monthRevenue, setMonthRevenue] = useState(0); // faturamento do período (pedidos)
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Active tab
  const [activeTab, setActiveTab] = useState("despesas");

  // Carregamento com cards (25/10/2026): até a lista (e as classificações)
  // chegar pela primeira vez, cards e tabela piscam em cinza em vez de mostrar
  // R$ 0,00 e "Nenhuma despesa". Recarregar depois de salvar não volta aqui.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [primeiraCargaClassificacoes, setPrimeiraCargaClassificacoes] = useState(true);
  
  // Nomes dos meses
  const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const monthNamesFull = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  
  useEffect(() => {
    fetchClassifications().finally(() => setPrimeiraCargaClassificacoes(false));
    fetchExpenses().finally(() => setPrimeiraCarga(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Faturamento do período: recarrega ao trocar mês/ano.
  useEffect(() => {
    fetchMonthRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  // Busca o faturamento (SUM pedidos.total, exceto cancelados) do mês selecionado
  // via /relatorios. Datas montadas com partes LOCAIS pra não deslocar o dia em UTC-3.
  const fetchMonthRevenue = async () => {
    try {
      setRevenueLoading(true);
      const pad = (n) => String(n).padStart(2, "0");
      const lastDay = new Date(selectedYear, selectedMonth + 1, 0).getDate();
      const dataInicio = `${selectedYear}-${pad(selectedMonth + 1)}-01`;
      const dataFim = `${selectedYear}-${pad(selectedMonth + 1)}-${pad(lastDay)}`;
      const response = await axios.get(
        `${API}/relatorios?data_inicio=${dataInicio}&data_fim=${dataFim}`,
        getAuthHeader()
      );
      setMonthRevenue(Number(response.data?.faturamento_periodo) || 0);
    } catch (error) {
      console.error("Erro ao carregar faturamento do período:", error);
      setMonthRevenue(0);
    } finally {
      setRevenueLoading(false);
    }
  };

  const fetchClassifications = async () => {
    try {
      const response = await axios.get(`${API}/expense-classifications`, getAuthHeader());
      setClassifications(response.data);
    } catch (error) {
      console.error("Erro ao carregar classificações:", error);
    }
  };
  
  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`, getAuthHeader());
      setExpenses(response.data);
    } catch (error) {
      console.error("Erro ao carregar despesas:", error);
    }
  };
  
  // ==================== CLASSIFICAÇÕES ====================
  
  const handleSaveClassification = async () => {
    if (!classificationName.trim()) {
      toast.error("Informe o nome da classificação");
      return;
    }
    if (!classificationNature) {
      toast.error("Escolha a natureza — é ela que define a linha do DRE");
      return;
    }
    
    try {
      if (editingClassification) {
        await axios.put(
          `${API}/expense-classifications/${editingClassification.id}`,
          { name: classificationName, nature: classificationNature },
          getAuthHeader()
        );
        toast.success("Classificação atualizada!");
      } else {
        await axios.post(
          `${API}/expense-classifications`,
          { name: classificationName, nature: classificationNature },
          getAuthHeader()
        );
        toast.success("Classificação criada!");
      }
      
      setClassificationDialogOpen(false);
      setClassificationName("");
      setClassificationNature("");
      setEditingClassification(null);
      fetchClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar classificação");
    }
  };
  
  const handleEditClassification = (classification) => {
    setEditingClassification(classification);
    setClassificationName(classification.name);
    // Sem carregar a natureza atual, abrir para renomear e salvar mandaria o
    // campo vazio e o backend recusaria (422).
    setClassificationNature(classification.nature || "");
    setClassificationDialogOpen(true);
  };
  
  const handleDeleteClassification = async () => {
    if (!classificationToDelete) return;
    
    try {
      await axios.delete(
        `${API}/expense-classifications/${classificationToDelete.id}`,
        getAuthHeader()
      );
      toast.success("Classificação excluída!");
      setDeleteClassificationDialogOpen(false);
      setClassificationToDelete(null);
      fetchClassifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir classificação");
    }
  };
  
  const handleInitializeClassifications = async () => {
    try {
      const response = await axios.post(
        `${API}/expense-classifications/initialize`,
        {},
        getAuthHeader()
      );
      if (response.data.created.length > 0) {
        toast.success(`${response.data.created.length} classificações criadas!`);
        fetchClassifications();
      } else {
        toast.info("Todas as classificações padrão já existem");
      }
    } catch (error) {
      toast.error("Erro ao inicializar classificações");
    }
  };
  
  // ==================== DESPESAS ====================
  
  // Nova e editar abrem a tela inteira (features/despesas/nova), levando o mês
  // aberto para a lista voltar nele depois de salvar.
  const handleOpenNewExpense = () => navigate(ROTA_NOVA_DESPESA, { state: { mes: selectedMonth, ano: selectedYear } });
  const handleEditExpense = (expense) => navigate(rotaEditarDespesa(expense.id), { state: { mes: selectedMonth, ano: selectedYear } });
  
  const handleTogglePaid = async (expense) => {
    try {
      await axios.patch(
        `${API}/expenses/${expense.id}/toggle-paid`,
        {},
        getAuthHeader()
      );
      toast.success(expense.is_paid ? "Despesa marcada como pendente" : "Despesa marcada como paga");
      fetchExpenses();
      
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };
  
  const handleDeleteExpense = async (deleteAll = false) => {
    if (!expenseToDelete) return;
    
    try {
      const response = await axios.delete(
        `${API}/expenses/${expenseToDelete.id}?delete_children=${deleteAll}`,
        getAuthHeader()
      );
      
      const deletedCount = response.data?.deleted_count || 1;
      if (deleteAll && deletedCount > 1) {
        toast.success(`${deletedCount} despesas excluídas!`);
      } else {
        toast.success("Despesa excluída!");
      }
      
      setDeleteExpenseDialogOpen(false);
      setDeleteRecurringDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
      
    } catch (error) {
      console.error("Erro ao excluir despesa:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Erro ao excluir despesa");
    }
  };
  
  // Verifica se a despesa é recorrente ou parcelada
  const isRecurringOrInstallment = (expense) => {
    if (!expense) return false;
    return expense.is_recurring || expense.installments_total > 1 || expense.parent_expense_id;
  };
  
  // Handler para abrir o dialog de exclusão
  const handleOpenDeleteDialog = (expense) => {
    setExpenseToDelete(expense);
    
    // Se a despesa é recorrente ou parcelada, mostra popup com 3 opções
    if (isRecurringOrInstallment(expense)) {
      setDeleteRecurringDialogOpen(true);
    } else {
      // Despesa normal - popup simples de confirmação
      setDeleteExpenseDialogOpen(true);
    }
  };
  
  // ==================== FILTROS E AGRUPAMENTO ====================
  
  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    
    // Filtro por texto
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(e => 
        e.name.toLowerCase().includes(term) ||
        (e.supplier && e.supplier.toLowerCase().includes(term)) ||
        (e.classification_name && e.classification_name.toLowerCase().includes(term))
      );
    }
    
    // Filtro por mês selecionado
    result = result.filter(e => {
      const date = new Date(e.due_date + "T00:00:00");
      return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
    });
    
    // Filtro por status
    if (statusFilter !== "all") {
      result = result.filter(e => statusFilter === "paid" ? e.is_paid : !e.is_paid);
    }
    
    // Filtro por classificação
    if (classificationFilter !== "all") {
      result = result.filter(e => e.classification_id === classificationFilter);
    }
    
    // Ordenar por data: mais recente em cima, mais antiga embaixo.
    // Desempate por created_at (dentro do mesmo dia, a mais recém-lançada primeiro).
    result.sort((a, b) => {
      const byDate = (b.due_date || "").localeCompare(a.due_date || "");
      if (byDate !== 0) return byDate;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    
    return result;
  }, [expenses, searchTerm, statusFilter, classificationFilter, selectedMonth, selectedYear]);
  
  // Estatísticas do mês selecionado
  const monthStats = useMemo(() => {
    let total = 0;
    let pendingCount = 0;
    let pendingValue = 0;
    let paidCount = 0;
    let paidValue = 0;
    
    filteredExpenses.forEach(e => {
      total++;
      if (e.is_paid) {
        paidCount++;
        paidValue += e.value;
      } else {
        pendingCount++;
        pendingValue += e.value;
      }
    });
    
    return { total, pendingCount, pendingValue, paidCount, paidValue };
  }, [filteredExpenses]);

  // Total de contas PAGAS do mês, independente dos filtros de busca/status/classificação.
  // Usado no Lucro pra ele refletir o mês inteiro (não muda quando o usuário filtra a lista).
  const monthPaidTotal = useMemo(() => {
    return expenses
      .filter((e) => {
        if (!e.is_paid) return false;
        const date = new Date(e.due_date + "T00:00:00");
        return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
      })
      .reduce((sum, e) => sum + (e.value || 0), 0);
  }, [expenses, selectedMonth, selectedYear]);

  // Lucro do período = Faturamento − contas pagas.
  const monthProfit = monthRevenue - monthPaidTotal;
  
  // Paginação
  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredExpenses.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredExpenses, currentPage, itemsPerPage]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, classificationFilter, selectedMonth, selectedYear]);
  
  // ==================== HELPERS ====================
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("pt-BR");
  };
  
  const isOverdue = (expense) => {
    if (expense.is_paid) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(expense.due_date + "T00:00:00");
    return dueDate < today;
  };
  
  // ==================== RENDER ====================
  
  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Despesas</h1>
            <p className="text-muted-foreground mt-1">Gerencie as despesas do seu estabelecimento</p>
          </div>
        </div>
        
        {/* Filtro de Meses - 12 meses visíveis */}
        <div className="bg-card rounded-xl border p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="font-medium">Selecione o Mês</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedYear(selectedYear - 1)}
              >
                &lt;
              </Button>
              <span className="font-bold text-lg w-16 text-center">{selectedYear}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedYear(selectedYear + 1)}
              >
                &gt;
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
            {monthNames.map((month, index) => (
              <Button
                key={index}
                variant={selectedMonth === index ? "default" : "outline"}
                size="sm"
                className={`${selectedMonth === index ? "" : "hover:bg-muted"}`}
                onClick={() => setSelectedMonth(index)}
              >
                {month}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Faturamento e Lucro do período */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2 text-blue-400 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Faturamento do Período</span>
            </div>
            {revenueLoading ? <Skeleton className="h-8 w-36" /> : (
              <p className="text-2xl font-bold text-blue-400">{formatCurrency(monthRevenue)}</p>
            )}
            <p className="text-xs text-muted-foreground">{monthNamesFull[selectedMonth]} {selectedYear}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className={`flex items-center gap-2 mb-1 ${monthProfit >= 0 ? "text-green-600" : "text-red-500"}`}>
              <Wallet className="w-4 h-4" />
              <span className="text-sm">Lucro do Período</span>
            </div>
            {revenueLoading || primeiraCarga ? <Skeleton className="h-8 w-36" /> : (
              <p className={`text-2xl font-bold ${monthProfit >= 0 ? "text-green-600" : "text-red-500"}`}>{formatCurrency(monthProfit)}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Faturamento − contas pagas ({primeiraCarga ? "…" : formatCurrency(monthPaidTotal)})
            </p>
          </div>
        </div>

        {/* Estatísticas do mês selecionado */}
        {primeiraCarga ? (
          <CarregandoCards quantos={4} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-sm">Total de Despesas</span>
            </div>
            <p className="text-2xl font-bold">{monthStats.total}</p>
            <p className="text-xs text-muted-foreground">{monthNamesFull[selectedMonth]} {selectedYear}</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Pendentes</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(monthStats.pendingValue)}</p>
            <p className="text-xs text-muted-foreground">{monthStats.pendingCount} despesas</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm">Pagas</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(monthStats.paidValue)}</p>
            <p className="text-xs text-muted-foreground">{monthStats.paidCount} despesas</p>
          </div>
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-center gap-2 text-primary mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Geral</span>
            </div>
            <p className="text-2xl font-bold text-primary">{formatCurrency(monthStats.pendingValue + monthStats.paidValue)}</p>
          </div>
        </div>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="despesas">Despesas</TabsTrigger>
              <TabsTrigger value="classificacao">Classificação</TabsTrigger>
            </TabsList>
            
            {activeTab === "despesas" && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  const dataToExport = filteredExpenses.map(e => ({
                    nome: e.name,
                    classificacao: e.classification_name || "",
                    fornecedor: e.supplier || "",
                    valor: e.value,
                    vencimento: e.due_date,
                    status: e.is_paid ? "Pago" : "Pendente",
                    data_pagamento: e.paid_date || ""
                  }));
                  exportToExcel(dataToExport, `despesas_${monthNamesFull[selectedMonth]}_${selectedYear}`, {
                    nome: "Nome",
                    classificacao: "Classificação",
                    fornecedor: "Fornecedor",
                    valor: "Valor",
                    vencimento: "Vencimento",
                    status: "Status",
                    data_pagamento: "Data Pagamento"
                  });
                }}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar
                </Button>
                <Button onClick={handleOpenNewExpense}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Despesa
                </Button>
              </div>
            )}
            
            {activeTab === "classificacao" && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleInitializeClassifications}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Criar Padrões
                </Button>
                <Button onClick={() => {
                  setEditingClassification(null);
                  setClassificationName("");
                  // sem isto a natureza da ultima edicao vazaria pro cadastro novo
                  setClassificationNature("");
                  setClassificationDialogOpen(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Classificação
                </Button>
              </div>
            )}
          </div>
          
          {/* Tab Despesas */}
          <TabsContent value="despesas" className="mt-0">
            {/* Filtros */}
            <div className="bg-card rounded-xl border p-4 mb-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar despesa..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                      <SelectItem value="paid">Pagos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select value={classificationFilter} onValueChange={setClassificationFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Classificação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas Classificações</SelectItem>
                      {classifications.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Lista de Despesas do mês selecionado */}
            <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
              {primeiraCarga ? (
                <CarregandoTabela linhas={8} colunas={7} className="overflow-hidden" />
              ) : paginatedExpenses.length === 0 ? (
                <div className="p-12 text-center">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-muted-foreground">Nenhuma despesa em {monthNamesFull[selectedMonth]} de {selectedYear}</p>
                  <Button variant="outline" className="mt-4" onClick={handleOpenNewExpense}>
                    <Plus className="w-4 h-4 mr-2" />
                    Cadastrar Despesa
                  </Button>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Despesa</TableHead>
                        <TableHead>Classificação</TableHead>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="w-24">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedExpenses.map(expense => (
                        <TableRow key={expense.id} className={isOverdue(expense) ? "bg-red-50 dark:bg-red-950/20" : ""}>
                          <TableCell>
                            <button
                              onClick={() => handleTogglePaid(expense)}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                expense.is_paid 
                                  ? "bg-green-500 border-green-500 text-white" 
                                  : "border-muted-foreground/30 hover:border-green-500"
                              }`}
                            >
                              {expense.is_paid && <Check className="w-4 h-4" />}
                            </button>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{expense.name}</p>
                              {expense.is_recurring && (
                                <span className="text-xs text-blue-600 flex items-center gap-1">
                                  <RefreshCw className="w-3 h-3" />
                                  Recorrente
                                </span>
                              )}
                              {expense.installments_total > 0 && (
                                <span className="text-xs text-purple-600 flex items-center gap-1">
                                  <CreditCard className="w-3 h-3" />
                                  Parcela {expense.installment_number}/{expense.installments_total}
                                </span>
                              )}
                              {expense.modulos?.length > 0 && (
                                <span className="flex flex-wrap gap-1 mt-1" title="Módulos a que a despesa foi atribuída">
                                  {expense.modulos.map((k) => {
                                    const canal = canalPorChave(k);
                                    return (
                                      <span key={k} className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                                        <IconeCanal canal={canal} className="w-3 h-3" />{canal.nome}
                                      </span>
                                    );
                                  })}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {expense.classification_name ? (
                              <span className="inline-block px-2 py-1 bg-muted rounded-full text-xs whitespace-nowrap">
                                {expense.classification_name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {expense.supplier ? (
                              <span className="flex items-center gap-1 text-sm">
                                <Building2 className="w-3 h-3 text-muted-foreground" />
                                {expense.supplier}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            {formatCurrency(expense.value)}
                          </TableCell>
                          <TableCell>
                            <span className={`flex items-center gap-1 text-sm ${isOverdue(expense) ? "text-red-600 font-medium" : ""}`}>
                              <Calendar className="w-3 h-3" />
                              {formatDate(expense.due_date)}
                              {isOverdue(expense) && <AlertCircle className="w-3 h-3" />}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {expense.is_paid ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                Pago
                              </span>
                            ) : (
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                isOverdue(expense) 
                                  ? "bg-red-100 text-red-700" 
                                  : "bg-amber-100 text-amber-700"
                              }`}>
                                {isOverdue(expense) ? "Vencido" : "Pendente"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditExpense(expense)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleOpenDeleteDialog(expense)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Paginação */}
                  {filteredExpenses.length > itemsPerPage && (
                    <div className="p-4 border-t">
                      <TablePagination
                        currentPage={currentPage}
                        totalItems={filteredExpenses.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => {
                          setItemsPerPage(value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
          
          {/* Tab Classificações */}
          <TabsContent value="classificacao" className="mt-0">
            <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
              {primeiraCargaClassificacoes || primeiraCarga ? (
                <CarregandoTabela linhas={6} colunas={3} className="overflow-hidden" />
              ) : classifications.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Nenhuma classificação cadastrada</p>
                  <Button variant="outline" className="mt-4" onClick={handleInitializeClassifications}>
                    Criar Classificações Padrão
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead className="text-center">Despesas</TableHead>
                      <TableHead className="w-24">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classifications.map(classification => {
                      const expenseCount = expenses.filter(e => e.classification_id === classification.id).length;
                      return (
                        <TableRow key={classification.id}>
                          <TableCell className="font-medium">{classification.name}</TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-muted rounded-full text-sm">
                              {expenseCount}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditClassification(classification)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => {
                                  setClassificationToDelete(classification);
                                  setDeleteClassificationDialogOpen(true);
                                }}
                                disabled={expenseCount > 0}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Dialog Classificação */}
        <Dialog open={classificationDialogOpen} onOpenChange={setClassificationDialogOpen}>
          {/* Sem descrição de propósito: o aria-describedby vazio cala o aviso do Radix no console */}
          <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                {editingClassification ? "Editar Classificação" : "Nova Classificação"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nome da Classificação</Label>
                <Input
                  value={classificationName}
                  onChange={(e) => setClassificationName(e.target.value)}
                  placeholder="Ex: Energia, Água, Aluguel..."
                  className="mt-1"
                />
              </div>
              <SeletorNatureza
                valor={classificationNature}
                onChange={setClassificationNature}
              />
              <Button onClick={handleSaveClassification} className="w-full">
                {editingClassification ? "Salvar Alterações" : "Criar Classificação"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Dialog Confirmar Exclusão Classificação */}
        <AlertDialog open={deleteClassificationDialogOpen} onOpenChange={setDeleteClassificationDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Classificação?</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir a classificação "{classificationToDelete?.name}"? 
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteClassification} className="bg-destructive text-destructive-foreground">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Dialog Confirmar Exclusão Despesa (simples) */}
        <AlertDialog open={deleteExpenseDialogOpen} onOpenChange={setDeleteExpenseDialogOpen}>
          <AlertDialogContent className="sm:max-w-md">
            <AlertDialogHeader className="text-center sm:text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <AlertDialogTitle className="text-xl">Excluir Despesa</AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                Tem certeza que deseja excluir a despesa{" "}
                <span className="font-semibold text-foreground">"{expenseToDelete?.name}"</span>?
                <br />
                <span className="text-xs mt-2 block">Esta ação não pode ser desfeita.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="sm:justify-center gap-2 mt-4">
              <AlertDialogCancel className="sm:w-32">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleDeleteExpense(false)} 
                className="bg-red-600 hover:bg-red-700 sm:w-32"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Dialog Confirmar Exclusão Despesa Recorrente/Parcelada */}
        <AlertDialog open={deleteRecurringDialogOpen} onOpenChange={setDeleteRecurringDialogOpen}>
          <AlertDialogContent className="sm:max-w-lg">
            <AlertDialogHeader className="text-center sm:text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                <RefreshCw className="w-6 h-6 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-xl">Excluir Despesa Recorrente</AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                A despesa <span className="font-semibold text-foreground">"{expenseToDelete?.name}"</span> faz parte de uma série recorrente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="grid gap-3 py-4">
              <button
                onClick={() => handleDeleteExpense(false)}
                className="flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-primary hover:bg-primary/5 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10">
                  <Trash2 className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Excluir somente esta</p>
                  <p className="text-sm text-muted-foreground">Mantém as outras despesas da série</p>
                </div>
              </button>
              
              <button
                onClick={() => handleDeleteExpense(true)}
                className="flex items-center gap-4 p-4 rounded-lg border-2 border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 hover:border-red-400 hover:bg-red-100 dark:hover:bg-red-950/50 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-red-700 dark:text-red-400">Excluir todas as recorrentes</p>
                  <p className="text-sm text-red-600/70 dark:text-red-400/70">Esta e todas as despesas futuras serão excluídas</p>
                </div>
              </button>
            </div>
            
            <AlertDialogFooter className="sm:justify-center">
              <AlertDialogCancel className="w-full sm:w-auto">Cancelar</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
